import { Component, OnInit, inject, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { CompanyProductDeploy } from 'src/app/models/company_product_deploy.model';
import { CompanyProductDeployConfiguration } from 'src/app/models/company_product_deploy_configuration.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ApiService } from 'src/app/services/api.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { DatePipe, CommonModule } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { CompanyProductDeployConfigurationDialogComponent } from './company-product-deploy-configuration-dialog/company-product-deploy-configuration-dialog.component';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { MatChipsModule } from '@angular/material/chips';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';

interface CompanyProductDeployDisplay extends CompanyProductDeploy {
  _display: {
    environmentRole: string | null;
    environmentRoleLabel: string;
    environmentRoleClass: string;
    branch: string | null;
    tag: string | null;
    taskCodes: string;
    statusLabel: string;
    statusChipClass: string;
    applicationName: string;
  };
}

@Component({
  selector: 'app-company-product-deploys',
  templateUrl: './company-product-deploys.component.html',
  styleUrls: ['./company-product-deploys.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterLink,
    MatIcon,
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatIconButton,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    DatePipe,
    MatSelect,
    MatOption,
    MatChipsModule,
    CompanyProductPageIdentityComponent,
  ],
})
export class CompanyProductDeploysComponent extends IndexTemplateComponent {
  private api = inject(ApiService);
  private permissions = inject(PermissionsService);
  private dialog = inject(MatDialog);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  company_product_id: string = null as any;

  displayed_columns: string[] = ['date', 'application', 'branch_env_tag', 'status', 'user', 'tasks', 'actions'];
  data_source: MatTableDataSource<CompanyProductDeployDisplay> = new MatTableDataSource<CompanyProductDeployDisplay>();

  configurations: CompanyProductDeployConfiguration[] = [];
  is_product_manager: boolean = false;

  private readonly configurations_bot_missing = signal(false);
  private readonly internal_team_bot_missing = signal(false);

  readonly has_bot_not_member_conversation = computed((): boolean => this.configurations_bot_missing() || this.internal_team_bot_missing());

  override filterFormExtraParams: any = {
    status: '',
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    const parent_route = this.activatedRoute.parent?.snapshot;
    this.company_product_id = (parent_route?.paramMap.get('company_product_id') ??
      parent_route?.paramMap.get('id') ??
      this.activatedRoute.parent?.snapshot.parent?.paramMap.get('company_product_id') ??
      this.activatedRoute.parent?.snapshot.parent?.paramMap.get('id')) as string;
    super.ngOnInit();
    this.checkPermissions();
    if (this.is_product_manager || this.permissions.isAdmin()) {
      this.loadConfigurations();
    }
  }

  override fetchData() {
    this.api
      .getCompanyProductDeploys(this.company_product_id, {
        ...this.filterForm.value,
        order_by: 'created_at:desc',
        with: 'application_environment.application,user,product_tasks',
      })
      .subscribe({
        next: (data: ArrayResponse<CompanyProductDeploy>) => {
          const processedData = (data.data || []).map(deploy => this.processDeployForDisplay(deploy));
          this.data_source.data = processedData;

          // ArrayResponse siempre tiene meta
          if (data.meta) {
            this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
          } else {
            // Fallback si no hay meta (no debería pasar)
            this.setMetadata(data.data?.length || 0, 0, this.pageSize);
          }
        },
        error: error => {
          console.error('Error fetching company_product deploys:', error);
          this.data_source.data = [];
          this.setMetadata(0, 0, this.pageSize);
        },
      });
  }

  private processDeployForDisplay(deploy: CompanyProductDeploy): CompanyProductDeployDisplay {
    const environmentRole = deploy.application_environment?.role || null;
    const branch = deploy.head_branch || null;
    const tag = deploy.tag || null;

    return {
      ...deploy,
      _display: {
        environmentRole,
        environmentRoleLabel: this.getEnvironmentRoleLabelStatic(environmentRole),
        environmentRoleClass: this.getEnvironmentRoleClassStatic(environmentRole),
        branch,
        tag,
        taskCodes: this.getTaskCodesStatic(deploy),
        statusLabel: this.getStatusLabelStatic(deploy.status),
        statusChipClass: this.getStatusChipClassStatic(deploy.status),
        applicationName: deploy.application_environment?.application?.name || '-',
      },
    };
  }

  private getStatusLabelStatic(status: string): string {
    const labels: { [key: string]: string } = {
      pending: 'Pendiente',
      running: 'En ejecución',
      success: 'Exitoso',
      failure: 'Fallido',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  }

  private getStatusChipClassStatic(status: string): string {
    const classes: { [key: string]: string } = {
      pending: 'mat-yellow',
      running: 'mat-accent',
      success: 'mat-success',
      failure: 'mat-warn',
      cancelled: 'mat-grey',
    };
    return classes[status] || 'mat-grey';
  }

  private getTaskCodesStatic(deploy: CompanyProductDeploy): string {
    if (!deploy.product_tasks || deploy.product_tasks.length === 0) {
      return '-';
    }
    return deploy.product_tasks.map(t => t.code).join(', ');
  }

  private getEnvironmentRoleLabelStatic(role: string | null): string {
    if (!role) return '';
    const labels: { [key: string]: string } = {
      production: 'Producción',
      staging: 'Staging',
      development: 'Desarrollo',
      other: 'Otro',
    };
    return labels[role] || role;
  }

  private getEnvironmentRoleClassStatic(role: string | null): string {
    if (!role) return '';
    const classes: { [key: string]: string } = {
      production: 'mat-accent',
      staging: 'mat-grey',
      development: 'mat-grey',
      other: 'mat-grey',
    };
    return classes[role] || 'mat-grey';
  }

  openCreateConfigurationDialog(): void {
    this.api
      .getCompanyProduct(this.company_product_id, { with: 'company,internal_team_messaging_conversation' })
      .subscribe(company_product => {
        this.api
          .getMessagingConversations({ company_id: company_product.company_id, per_page: 200, scopes: 'active' })
          .subscribe(channels_response => {
            this.dialog
              .open(CompanyProductDeployConfigurationDialogComponent, {
                width: '720px',
                maxWidth: '92vw',
                data: {
                  companyProductId: this.company_product_id,
                  companyId: company_product.company_id,
                  messagingConversations: channels_response.data || [],
                  internalTeamMessagingConversation: company_product.internal_team_messaging_conversation ?? null,
                },
              })
              .afterClosed()
              .subscribe(result => {
                if (result) {
                  this.loadConfigurations();
                }
              });
          });
      });
  }

  getNotifiableName(config: CompanyProductDeployConfiguration): string {
    if (!config.notifiable) {
      return 'Desconocido';
    }

    if (config.notifiable_type === 'App\\Models\\User') {
      const user = config.notifiable as any;
      return user.full_name || user.name || 'Usuario';
    } else {
      const conversation = config.notifiable as MessagingConversation;
      return conversation.display_name || 'Conversación';
    }
  }

  private checkPermissions() {
    this.is_product_manager = this.permissions.isBisualProductionTeamInCompanyProductWithRole(this.company_product_id, 'product-manager');
  }

  private loadConfigurations() {
    this.api.getCompanyProductDeployConfigurations(this.company_product_id, { with: 'notifiable' }).subscribe(data => {
      this.configurations = data;
      this.configurations_bot_missing.set(
        data.some((config): boolean => {
          if (config.notifiable_type !== 'App\\Models\\MessagingConversation') {
            return false;
          }

          const conversation = config.notifiable as MessagingConversation | undefined;
          return conversation != null && conversation.bot_is_member === false;
        })
      );
    });

    this.api.getCompanyProduct(this.company_product_id, { with: 'internal_team_messaging_conversation' }).subscribe(company_product => {
      const internal = company_product.internal_team_messaging_conversation;
      this.internal_team_bot_missing.set(internal != null && internal.bot_is_member === false);
    });
  }
}
